import { useState } from 'react'
import { fuzzyMatch } from '../lib/fuzzyMatch'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Search as SearchIcon } from 'lucide-react'
import { APP_VERSION } from '../lib/appVersion'
import { COACHMARKS, resetCoach } from '../lib/coachmarks'
import { DossierAccordion } from '../components/Interactions'

const SECTIONS = [
  // v17 (2026-09-15): rewritten around the tracker. Cut features (For You, Canon,
  // Shelves, Buzz, Reviews room, Challenges, Roll Credits, AI recaps) are gone from
  // here because they are gone from the nav; shelved features say where their toggle is.
  {
    emoji: '🎬',
    title: 'Getting Started',
    items: [
      {
        q: 'What is NextEp?',
        a: 'NextEp is a fast TV tracker. Add the shows you watch, mark episodes as you go, and always know what to watch next and when the next one airs. Everything else — games, friends, discussion rooms — is optional and off until you turn it on in Settings → Features.',
      },
      {
        q: 'How do I add a show?',
        a: 'Tap Search (the magnifying glass on Home, or the + menu) and type the title. Tap the show, then "Add to my list." You can also browse Discover by mood, genre and streaming service, or import your whole history from another app.',
      },
      {
        q: 'I added a lot of shows and they all start at S1·E1. Now what?',
        a: 'Tell NextEp where you are. Right after an add it asks; if you skipped that, My List shows a "Set where I am" banner once several shows are still at the start, and walks you through them one at a time. You can also open any show and tap its status to pick the episode you are on.',
      },
      {
        q: 'Do I need an account?',
        a: 'No. Start as a guest and add shows straight away. Your list is yours for 30 days on that phone without an account; after your first mark NextEp offers to keep it anywhere with one email, no password. On day 30 the list goes read-only (never deleted) until you add an email.',
      },
      {
        q: 'What does “N days left on this phone” mean?',
        a: 'That is the guest lease. A guest list lives in this browser only, and Safari clears that storage after a week away unless NextEp is on your Home Screen. Add an email from Profile or the keep-this-list sheet and the countdown disappears for good.',
      },
    ],
  },
  {
    emoji: '🃏',
    title: 'Home: the deck',
    items: [
      {
        q: 'What is the card on Home?',
        a: 'It is the next episode you should watch, across every show you are following. The card behind it is the show after that. Below the deck, "N more waiting" lists what else has an episode ready.',
      },
      {
        q: 'How do I mark the episode watched?',
        a: 'Swipe the card left. An amber ring draws as you go, the clapboard slams past the line, and when you let go the episode is marked and the next card rises. A quick flick works too. An Undo bar appears at the bottom for a few seconds if it was a mistake.',
      },
      {
        q: 'How do I rate an episode?',
        a: 'Mark it watched (the Watched button under the Home card, a swipe left, or the show page) and a card slides up asking how it was: five reels, half steps included. You can also rate any watched episode late from its ring on the show page.',
      },
      {
        q: 'How do I get to a different show?',
        a: 'Swipe the card right to turn the deck, or tap one of the rows under it. The ⋯ in the card\'s corner opens the show\'s page.',
      },
      {
        q: 'What does "You\'re caught up" mean?',
        a: 'Every show you follow has no unwatched aired episode. Home then shows the next few air times so you know what is coming, and points you to Discover for something new.',
      },
    ],
  },
  {
    emoji: '📺',
    title: 'The show page',
    items: [
      {
        q: 'How do I mark episodes on a show\'s page?',
        a: 'Each episode has a ring. Tap the ring to mark that episode watched. Tap a ring further down and everything between your position and it is marked in one go; you will be asked to rate the run if you want to.',
      },
      {
        q: 'What is scrubbing?',
        a: 'Press a ring and drag down the list. The rings fill in a wave as you go and a chip counts "Mark 4 episodes." Let go to commit. It is the fastest way to log a binge.',
      },
      {
        q: 'I marked too far. How do I go back?',
        a: 'Tap any filled ring to rewind your position to that episode: it and everything after it read unwatched again. An Undo pill also appears right after every mark.',
      },
      {
        q: 'Where did the cast, ratings and where-to-watch go?',
        a: 'Behind the ⋯ button at the top right of the show page. One sheet holds About & cast, Ratings & reviews, Where to watch, Watch together (when People is on) and Remove from list.',
      },
      {
        q: 'What does the status line under the title mean?',
        a: '"Returning · Wednesdays on Prime Video" means the show is still running and when it airs. "Ended · 3 seasons" means the run is complete. The progress line under it counts episodes watched out of the total.',
      },
    ],
  },
  {
    emoji: '🌙',
    title: 'Tonight & reminders',
    items: [
      {
        q: 'Where can I swipe?',
        a: 'Every list row works the same way: swipe right to put it on Tonight (the moon), swipe left to remove it. Marking an episode is always the clapboard tap. On Home, the deck is the exception: swipe the card left to mark it watched, right to turn to the next show, and tap it to rate.',
      },
      {
        q: 'What is Tonight?',
        a: 'Your plan for this evening. Pick the episodes you intend to watch and Tonight keeps them in one place, with a nudge on the moon icon after 5pm if nothing is planned yet.',
      },
      {
        q: 'Why is a show I follow missing from Upcoming?',
        a: 'Upcoming lists episodes with an announced date in the next two weeks. A show that is still running but has no date yet sits under "Between episodes" with its usual slot, and moves up the moment the network dates the next one. NextEp re-checks every show you follow daily, and again when you come back to the app after a few hours away.',
      },
      {
        q: 'What push notifications does NextEp send?',
        a: 'Episode reminders when a show you follow airs a new episode (once a day, in your timezone), a heads-up the night before if you turn that on, and a note when a show you finished announces its return. Friend activity pushes only exist when People is on.',
      },
      {
        q: 'How do I control notifications?',
        a: 'Profile → Settings → Notifications. Each type has its own switch and you can turn push off entirely there.',
      },
    ],
  },
  {
    emoji: '🎞️',
    title: 'Ratings & finishes',
    items: [
      {
        q: 'What are Reels?',
        a: 'NextEp\'s rating scale, one to five reels. Episodes are rated on the card that follows a mark, or late from the show page. A whole series is rated when you finish it.',
      },
      {
        q: 'Why are some episode rings brighter than others?',
        a: 'That’s the show’s quality map. An unwatched ring’s edge brightens with the episode’s community rating on TMDB, so a season’s sag or its peak reads at a glance, and a small amber dot marks the standouts (8.5 and up). Tap the episode to see the number. Episodes with too few votes stay plain.',
      },
      {
        q: 'What is a Final Cut card?',
        a: 'One card for every show you finish, on your profile. It carries the poster in the show’s own colour, your rating, your run (when you started and how many nights it took) and your one-line take if you left one in the episode conversation. One per show, no two alike, numbered in the order you finished them. Tap a card to share it as a picture.',
      },
      {
        q: 'What happens when I finish a season?',
        a: 'A short wrap moment: the show\'s colour fills the screen and you can tap one reaction that your friends can see (if People is on). Tap anywhere to continue.',
      },
      {
        q: 'What happens when I finish a series?',
        a: 'The Final Cut: "That\'s a wrap," five reels to rate the whole series, and a small record of your run — seasons, episodes, hours, and the dates you started and finished. It is saved as a keepsake in your Profile.',
      },
    ],
  },
  {
    emoji: '📥',
    title: 'Importing & your data',
    items: [
      {
        q: 'Coming from TV Time, Letterboxd or another app?',
        a: 'Go to Settings → Import & Sync → "Import from another app." Export your shows as a CSV from TV Time, Letterboxd, IMDb, Simkl, or Trakt, then upload the file (or paste it). NextEp matches each title to the right show automatically, no TMDB IDs needed, and brings your ratings across too. Choose "Already Watched" to mark them complete or "Plan to Watch" to add them to your list.',
      },
      {
        q: 'Can I import from Trakt or Plex specifically?',
        a: 'Yes. Trakt\'s own full data export works as-is: in Trakt go to Settings → Data → "Export now", then upload the ZIP it downloads to NextEp\'s importer. Every episode you played lands at the right position, with your show ratings, watchlist and hidden shows carried across (movies are skipped). Settings → Import & Sync also has a dedicated "Import from Trakt" (paste a Trakt CSV export) and a "Connect Plex" sync for your Plex TV library, alongside the universal importer and the screenshot import (scan any streaming app\'s watchlist with a photo).',
      },
      {
        q: 'Can NextEp track episodes automatically as I watch on Plex or Jellyfin?',
        a: 'Yes. After connecting Plex or Jellyfin (Settings → Import & Sync), open "Auto-track new episodes as you watch" to get a webhook URL. Paste it into Plex\'s Webhooks settings (requires Plex Pass) or into Jellyfin\'s Webhook plugin, and NextEp advances your progress the moment you finish an episode. It only moves shows already on your list, and only forward.',
      },
      {
        q: 'Can I export my data / is my collection mine?',
        a: 'Always. Settings → Account → "Export JSON" gives you a full backup of your library, reviews and favorites; "Export CSV" gives you a spreadsheet of your library. Your collection is yours to keep and take anywhere.',
      },
    ],
  },
  {
    emoji: '🎛️',
    title: 'Features & toggles',
    items: [
      {
        q: 'Why is NextEp so simple by default?',
        a: 'Because most people want a tracker. New accounts start in Basic: Home, your list, show pages, Tonight, Discover, Import and Stats. Everything else is a switch in Settings → Features so the app only grows when you ask it to.',
      },
      {
        q: 'What can I turn on?',
        a: 'Cliffhanger, Face-Off and Verdict (daily games: guess the show from clues, higher-or-lower between two posters, and five gut calls about one show scored by how close you land), People & Social (follow friends, see who is on the same episode, watch together), The Aftershow (spoiler-safe discussion rooms per show), and Wrapped & Rewind (your year in TV). Your run up the ladder (Pilot, Regular, Season Pass…) is always on; it lives on Profile and Stats.',
      },
      {
        q: 'Where did Canon, Shelves, Buzz, For You and Challenges go?',
        a: 'They were retired in version 17. Your data from them is kept, nothing was deleted, but the pages are no longer part of the app.',
      },
    ],
  },
  {
    emoji: '👥',
    title: 'People & watching together',
    items: [
      {
        q: 'How do I follow someone?',
        a: 'Turn on People & Social in Settings → Features, then open People from the + menu or search a username from Search. Following is one tap and needs no approval.',
      },
      {
        q: 'What does the friend line on the Home card mean?',
        a: 'When someone you follow is watching the same show, the card says whether they are on the same episode, ahead, or behind. Tap it to open their profile.',
      },
      {
        q: 'What is Watch Together?',
        a: 'From a show\'s ⋯ sheet, invite a friend to the same episode. Both of you get a lobby and a live room to react in while you watch.',
      },
      {
        q: 'Who can see my activity?',
        a: 'Only people who follow you, and only if "Share my activity" is on in Settings → Notifications. Guests and Basic accounts share nothing.',
      },
    ],
  },
  {
    emoji: '📱',
    title: 'App & settings',
    items: [
      {
        q: 'How do I add NextEp to my iPhone home screen?',
        a: 'In Safari, tap the ··· button at the bottom, then Share, then View More, then "Add to Home Screen", then Add. It opens full-screen like a native app, gets push notifications, and your list stops depending on Safari storage. On Android, Profile → "Put NextEp on your Home Screen" installs it in one tap.',
      },
      {
        q: 'A show is airing but I can’t watch it anywhere. What do I do?',
        a: 'On the card’s ⋯ menu or the show page, choose “Can’t watch it yet”. The show leaves your deck without being abandoned and sits in My List under “Waiting for a way to watch”. The day it turns up on one of your services, it comes back by itself and tells you where it landed.',
      },
      {
        q: 'What is the Live wall?',
        a: 'A public page (More → Live wall, or nextep.show/live) showing what people on NextEp are watching, marking and finishing this week, plus today\u2019s Cliffhanger, Face-Off and Verdict cases with how the crowd did. Only people who chose a public profile appear. Signed in, every show carries where you are with it.',
      },
      {
        q: 'Is NextEp on the Google Play Store?',
        a: 'Yes. Search "NextEp" on Google Play. It is the same app with notifications and home-screen presence built in.',
      },
      {
        q: 'How do I update the app?',
        a: 'Updates arrive on their own. When one is ready, a small popcorn pill appears at the top; tap it and the app reloads on the new version.',
      },
      {
        q: 'How do I share a show or my profile?',
        a: 'Shows: the ⋯ sheet on a show page has a share link. Profile: Profile → Share, which gives a link anyone can open without an account.',
      },
      {
        q: 'How do I report a bug or pitch an idea?',
        a: 'The amber button at the top of this page, or Profile → Report a bug or pitch an idea, or the + menu → Writers\' Room. It goes straight to the people who build NextEp.',
      },
    ],
  },
]

// DossierAccordion (design sync 2026-08-14, replaces this section's plain
// Collapse-per-row FAQ list): the gold spine + numeral-to-check treatment, one
// instance per section with its own open-set state so sections don't share state.
function SectionAccordion({ items }) {
  const [openKeys, setOpenKeys] = useState(() => new Set())
  const toggle = (key) => setOpenKeys(prev => {
    const next = new Set(prev)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })
  return (
    <DossierAccordion
      items={items.map(item => ({
        key: item.q, title: item.q,
        body: item.a.split('\n').map((line, i) => (
          <p key={i} style={{
            color: line === '' ? undefined : 'var(--text-muted)',
            fontSize: 13, lineHeight: 1.65, margin: line === '' ? '4px 0' : '0 0 2px',
          }}>{line}</p>
        )),
      }))}
      openKeys={openKeys}
      onToggle={toggle}
    />
  )
}

export default function HelpPage() {
  const navigate  = useNavigate()
  const [filter,  setFilter] = useState('')
  const [replayed, setReplayed] = useState(null)   // coachmark id just re-armed
  // The Almanac (design sync 2026-08-15): 20 sections stacked in one endless scroll
  // was the real problem here, not the visuals. A chapter tab-row shows one section
  // at a time; search still flattens across all of them, same as before.
  const [chapter, setChapter] = useState(SECTIONS[0].title)

  // Replay a coachmark: clear its "seen" flag so the in-context hint shows again.
  // If it points at a specific screen (route), take them there; otherwise confirm
  // inline that the pointer is back where it lives.
  const replayCoach = (c) => {
    resetCoach(c.id)
    if (c.route) { navigate(c.route); return }
    setReplayed(c.id)
  }

  const searching = filter.trim().length > 0
  const filtered = searching
    ? SECTIONS.map(s => ({
        ...s,
        items: s.items.filter(i =>
          fuzzyMatch(i.q, filter) || fuzzyMatch(i.a, filter)
        ),
      })).filter(s => s.items.length > 0)
    : SECTIONS.filter(s => s.title === chapter)

  return (
    <div className="surface-wide dsk-solo-page p17" style={{ minHeight: '100dvh', '--nxa': 'pageSlideUp 0.34s var(--ease-out) backwards', background: 'var(--bg)', fontFamily: 'var(--font)', paddingBottom: 'calc(var(--nav-h) + 16px)' }}>

      {/* Header */}
      <div style={{ padding: 'max(16px,env(safe-area-inset-top)) 16px 12px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 100 }}>
        <button onClick={() => navigate(-1)} aria-label="Back" className="ui-hit" style={{ background: 'var(--surface)', border: 'none', borderRadius:'var(--radius-full)', width: 34, height: 34, color: 'var(--text)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ArrowLeft size={16}/>
        </button>
        <div>
          <h1 style={{ margin: 0, fontFamily: 'var(--font-serif)', fontWeight: 500, fontSize: 28, lineHeight: 1.1, color: 'var(--text-strong)', letterSpacing: '-0.01em' }}>Help</h1>
        </div>
      </div>

      {/* Search */}
      <div style={{ padding: '12px 16px' }}>
        <div style={{ position: 'relative' }}>
          <SearchIcon size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }} />
          <input
            value={filter} onChange={e => setFilter(e.target.value)}
            placeholder="Search help topics…"
            style={{ width: '100%', padding: '10px 12px 10px 34px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius:'var(--radius)', color: 'var(--text)', fontSize: 16, boxSizing: 'border-box', fontFamily: 'var(--font)' }}
          />
          {filter && <button onClick={() => setFilter('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 18 }}>×</button>}
        </div>
      </div>

      {/* The bug / idea door, first thing under search: Help is where a stuck person lands. */}
      {!filter && (
        <button onClick={() => window.dispatchEvent(new CustomEvent('nextep:open-writers-room', { detail: { source: 'help' } }))}
          style={{ display: 'flex', alignItems: 'center', gap: 10, width: 'calc(100% - 32px)', margin: '0 16px 10px', padding: '12px 14px', background: 'var(--amber)', border: 'none', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontFamily: 'var(--font)', textAlign: 'left' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: 'var(--amber-ink)', fontWeight: 600, fontSize: 14 }}>Something broken? Have an idea?</div>
            <div style={{ color: 'rgba(0,0,0,0.65)', fontSize: 12.5, marginTop: 1 }}>Tell us. It goes straight to the people who build NextEp.</div>
          </div>
          <span style={{ color: 'var(--amber-ink)', fontSize: 15, fontWeight: 700 }}>→</span>
        </button>
      )}
      {/* Pointer to the full, scannable feature list on the marketing site */}
      {!filter && (
        <a href="https://nextep.show/features" target="_blank" rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 16px 12px', padding: '12px 14px', background: 'var(--bg-card)', border: '1px solid rgba(var(--amber-rgb),0.25)', borderRadius: 'var(--radius-md)', textDecoration: 'none', fontFamily: 'var(--font)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: 'var(--text-strong)', fontWeight: 600, fontSize: 13.5 }}>See everything NextEp does</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 1 }}>The full feature list, in one place</div>
          </div>
          <span style={{ color: 'var(--amber)', fontSize: 15, fontWeight: 700 }}>→</span>
        </a>
      )}

      {/* Discord community — real person answers, not a spreadsheet */}
      {!filter && (
        <a href="https://discord.gg/Km87DQHCSm" target="_blank" rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 16px 12px', padding: '12px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', textDecoration: 'none', fontFamily: 'var(--font)' }}>
          <span style={{ fontSize: 18 }}>💬</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: 'var(--text-strong)', fontWeight: 700, fontSize: 13.5 }}>Join the NextEp Discord</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 1 }}>Talk shows, get help straight from the developer, and shape what gets built next</div>
          </div>
          <span style={{ color: 'var(--text-dim)', fontSize: 15, fontWeight: 700 }}>→</span>
        </a>
      )}

      {/* Status dot legend, always visible */}
      {!filter && (
        <div style={{ margin: '0 16px 16px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius:'var(--radius-md)', padding: '14px 16px' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--amber)', marginBottom: 10 }}>Status colours</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px' }}>
            {[
              { color: 'var(--green)',              label: 'Watching' },
              { color: 'var(--amber)',              label: 'Completed' },
              { color: 'var(--purple)',                   label: 'Rewatching' },
              { color: 'var(--blue)',      label: 'Plan to Watch' },
              { color: 'var(--text-dim)',    label: 'Abandoned' },
            ].map(s => (
              <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <div style={{ width: 11, height: 11, borderRadius:'var(--radius-full)', background: s.color, border: '2px solid rgba(var(--bg-rgb),0.8)', flexShrink: 0 }}/>
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tips & Tutorials, replayable coachmarks (the in-app pointers) */}
      {!filter && (
        <div style={{ margin: '0 16px 16px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '14px 16px' }}>
          <div style={{ color: 'var(--amber)', fontWeight: 600, fontSize: 13 }}>💡 Tips &amp; Tutorials</div>
          <div style={{ color: 'var(--text-dim)', fontSize: 11.5, marginTop: 2 }}>New to NextEp? Replay a quick pointer any time.</div>
          {COACHMARKS.map(c => (
            <div key={c.id} style={{ paddingTop: 11, marginTop: 11, borderTop: '1px solid var(--border-soft)' }}>
              <div style={{ color: 'var(--text)', fontWeight: 600, fontSize: 13 }}>{c.title}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 12, lineHeight: 1.5, margin: '3px 0 9px' }}>{c.blurb}</div>
              {replayed === c.id ? (
                <div style={{ color: 'var(--green)', fontSize: 12, fontWeight: 600 }}>✓ Re-armed{c.where ? `, ${c.where}` : '.'}</div>
              ) : (
                <button onClick={() => replayCoach(c)} className="ui-hit" style={{ background: 'rgba(var(--amber-rgb),0.12)', border: '1px solid rgba(var(--amber-rgb),0.3)', borderRadius: 'var(--radius-xl)', padding: '6px 14px', color: 'var(--amber)', fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)' }}>
                  {c.route ? 'Show me →' : 'Show me again'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Chapter tabs — table of contents instead of scrolling past 20 stacked
          sections to find one. Hidden while searching, since search already
          flattens matches across every chapter into one result list below. */}
      {!searching && (
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none', padding: '0 16px 16px' }}>
          {SECTIONS.map(s => {
            const active = s.title === chapter
            return (
              <button key={s.title} onClick={() => setChapter(s.title)} aria-pressed={active} className="ui-hit" style={{
                flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontFamily: 'var(--font)',
                background: active ? 'rgba(var(--amber-rgb),0.16)' : 'var(--surface)',
                border: `1px solid ${active ? 'rgba(var(--amber-rgb),0.4)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-xl)', padding: '7px 14px', fontSize: 12, fontWeight: 700,
                color: active ? 'var(--amber)' : 'var(--text-dim)', whiteSpace: 'nowrap',
              }}>
                <span>{s.emoji}</span>{s.title}
              </button>
            )
          })}
        </div>
      )}

      {/* Sections — one chapter's accordion at a time, or every matching Q&A
          across all chapters while searching. */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px 24px', color: 'var(--text-muted)', fontSize: 14 }}>
          {searching ? `No results for "${filter}".` : 'Pick a chapter above.'}
          <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-dim)' }}>
            Still stuck? <a href="mailto:hello@nextep.show?subject=NextEp%20Help" style={{ color: 'var(--amber)', textDecoration: 'none', fontWeight: 600 }}>Email us →</a>
          </div>
        </div>
      ) : filtered.map(section => (
        <div key={section.title} style={{ margin: '0 16px 20px' }}>
          <div style={{ padding: '0 0 10px 2px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>{section.emoji}</span>
            <span style={{ color: 'var(--text)', fontWeight: 700, fontSize: 15 }}>{section.title}</span>
          </div>
          <SectionAccordion items={section.items} />
        </div>
      ))}

      {/* Still need help, a real human contact, above the footer */}
      <div style={{ margin: '4px 16px 20px', background: 'var(--bg-card)', border: '1px solid rgba(var(--amber-rgb),0.25)', borderRadius: 'var(--radius-md)', padding: '16px', textAlign: 'center' }}>
        <div style={{ fontSize: 22, marginBottom: 6 }}>💬</div>
        <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Still need help?</div>
        <div style={{ color: 'var(--text-dim)', fontSize: 12.5, lineHeight: 1.5, marginBottom: 12 }}>
          Can't find what you're looking for? We read every message.
        </div>
        <a href="mailto:hello@nextep.show?subject=NextEp%20Help" style={{
          display: 'inline-block', background: 'rgba(var(--amber-rgb),0.15)', border: '1px solid rgba(var(--amber-rgb),0.4)',
          borderRadius: 'var(--radius-sm)', padding: '9px 18px', color: 'var(--amber)', fontWeight: 700, fontSize: 13,
          textDecoration: 'none', fontFamily: 'var(--font)',
        }}>Contact us</a>
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', padding: '0 16px 20px', color: 'var(--text-dim)', fontSize: 12 }}>
        NextEp v{APP_VERSION} · Made with 🎬
      </div>
    </div>
  )
}
